/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDialProfileList.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Custom hook for managing Dial Profile list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useDataGrid, DataGridColumn } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchDialProfiles } from '../api';
import { DialProfile } from '../types';

interface UseDialProfileListProps {
  initialColumns?: DataGridColumn<DialProfile>[];
  active?: boolean;
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Custom React hook for managing the Dial Profiles DataGrid list.
 * Integrates directly with `useDataGrid` for pagination, sorting, and search capabilities.
 * Supports Domain Contextual Filtering to only show profiles belonging to the current tenant.
 * 
 * @param props - Configuration properties for the list hook.
 * @returns Combined DataGrid state and the effective Domain ID applied to the filter.
 */
export function useDialProfileList(props: UseDialProfileListProps = {}) {
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = props.domainId ?? selectedDomain?.id;

  const fetchProfiles = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const result = await fetchDialProfiles({
        ...params,
        active: props.active,
        domainId: effectiveDomainId,
      });
      return {
        data: result.data || [],
        pagination: result.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        },
      };
    } catch (error) {
      return {
        data: [] as DialProfile[],
        pagination: {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: 0,
        },
      };
    }
  }, [props.active, effectiveDomainId]);

  const dataGridResult = useDataGrid({
    fetchData: fetchProfiles,
    initialColumns: props.initialColumns || [],
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'dialProfiles',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && props.onRefreshNeeded) {
      props.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    ...dataGridResult,
    effectiveDomainId,
  };
}

